import { useState, useRef, useEffect, type ChangeEvent } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import toast from "react-hot-toast";
import UserProfileCard from "../../components/UserProfileCard";
import { useAuth } from "../../contexts/AuthContext";
import defaultProfileImage from "../../assets/profile-img.svg";
import { updateUser } from "../../api/users/user";
import { uploadThumbnail } from "../../api/upload/file";
import { validateUserForm, getProfileImageExtensionError, extractApiError } from "../../utils/util";
import type {
  UserFieldErrors,
  UserTouched,
} from "../../utils/interface/userListing";

/** Build the shape expected by validateUserForm (used in Add/Edit User) */
const toUserFormData = (
  fullName: string,
  email: string,
  companyName?: string,
  jobTitle?: string,
  phone?: string
) => ({
  firstName: fullName,
  lastName: "",
  email,
  companyName: companyName ?? "",
  jobTitle: jobTitle ?? "",
  phone: phone ?? "",
});

const ManageAccount = () => {
  const { userDetails, refreshUserDetails } = useAuth();
  const isAdmin = userDetails?.type === "0";
  const profileImg =
    userDetails?.profileImagePreview ?? userDetails?.profileImage ?? null;
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState<UserTouched>({});
  const [fieldErrors, setFieldErrors] = useState<UserFieldErrors>({});

  useEffect(() => {
    if (userDetails) {
      // Don't force a fake value ("User") into the input; it hides the placeholder and is confusing.
      // If name is missing, keep it empty and let the placeholder show.
      setFullName((userDetails.fullName ?? "").trim());
      setEmail(userDetails.email ?? "");
      setCompanyName(userDetails.companyName ?? "");
      setJobTitle(userDetails.jobTitle ?? "");
      setPhone(userDetails.phone ?? "");
    }
  }, [userDetails]);

  const [profileImageName, setProfileImageName] = useState("");
  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(
    null,
  );
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [profileImageError, setProfileImageError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const input = event.target;

    if (!file) {
      setProfileImageName("");
      setProfileImagePreview(null);
      setProfileImageFile(null);
      setProfileImageError(null);
      return;
    }

    const extensionError = getProfileImageExtensionError(file);
    if (extensionError) {
      setProfileImageError(extensionError);
      setProfileImageName("");
      setProfileImagePreview(null);
      setProfileImageFile(null);
      if (input) input.value = "";
      return;
    }

    setProfileImageError(null);
    setProfileImageName(file.name);
    setProfileImageFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setProfileImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const displayProfileImg =
    profileImagePreview ?? (profileImg?.trim() || defaultProfileImage);

  const runValidation = (
    nextFullName: string,
    nextEmail: string,
    nextPhone?: string
  ) => {
    setFieldErrors(
      validateUserForm(
        toUserFormData(
          nextFullName,
          nextEmail,
          companyName,
          jobTitle,
          nextPhone ?? phone
        )
      ),
    );
  };

  const handleChange =
    (field: "fullName" | "email" | "companyName" | "jobTitle" | "phone") =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (field === "fullName") {
        setFullName(value);
        setTouched((t) => ({ ...t, firstName: true }));
        runValidation(value, email);
      } else if (field === "email") {
        setEmail(value);
        setTouched((t) => ({ ...t, email: true }));
        runValidation(fullName, value);
      } else if (field === "companyName") setCompanyName(value);
      else if (field === "jobTitle") setJobTitle(value);
      else if (field === "phone") {
        const digitsOnly = value.replace(/\D/g, "");
        setPhone(digitsOnly);
        setTouched((t) => ({ ...t, phone: true }));
        runValidation(fullName, email, digitsOnly);
      }
    };

  const handleBlur = (field: "fullName" | "email") => () => {
    setTouched((t) => ({
      ...t,
      ...(field === "fullName" ? { firstName: true } : { email: true }),
    }));
    runValidation(fullName, email);
  };

  const handleSave = async () => {
    if (!userDetails?.id) return;
    const errors = validateUserForm(
      toUserFormData(fullName, email, companyName, jobTitle, phone)
    );
    setFieldErrors(errors);
    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      companyName: true,
      jobTitle: true,
      phone: true,
    });
    if (Object.keys(errors).length > 0) return;
    setSaving(true);
    try {
      let imageUrl: string | undefined;
      if (profileImageFile) {
        imageUrl = await uploadThumbnail(profileImageFile, "users");
      }
      await updateUser(userDetails.id, {
        fullName: fullName || undefined,
        ...(isAdmin && { email: email || undefined }),
        companyName: companyName || undefined,
        jobTitle: jobTitle || undefined,
        phone: phone || undefined,
        ...(imageUrl && { profileImage: imageUrl }),
      });
      await refreshUserDetails();
      toast.success("Profile updated successfully.");
    } catch (err) {
      toast.error(extractApiError(err) || "Failed to update profile.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="">
      <UserProfileCard manageAccountTo="/manage-account" />
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-[#E761A0] hover:underline font-semibold mb-3 text-lg"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Dashboard
      </Link>

      <div>
        <h1 className="text-[34px] lg:text-[38px] xl:text-[56px font-display font-light text-white mb-[15px] leading-[76px]">
          Manage Account
        </h1>
        <p className="text-white max-w-[863px] text-lg font-light">
          Update your profile information and account settings.
        </p>
      </div>

      <div className="flex flex-col-reverse xl:grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mt-[50px]">
        <div className="xl:max-w-[548px] space-y-4">
          <div>
            <label className="block text-base text-white mb-4 font-light">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={fullName}
              onChange={handleChange("fullName")}
              onBlur={handleBlur("fullName")}
              placeholder="Enter full name"
              className={`w-full h-[53px] px-4 border rounded-[27px] text-white font-light bg-transparent placeholder-gray-500 focus:ring-2 focus:ring-[#E761A0]/50 focus:outline-none transition-all duration-200 ${
                touched.firstName && fieldErrors.firstName
                  ? "border-red-500/50"
                  : "border-[#E761A0] hover:border-[#E761A0]"
              }`}
            />
            {touched.firstName && fieldErrors.firstName && (
              <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                <svg
                  className="w-4 h-4 shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                {fieldErrors.firstName}
              </p>
            )}
          </div>
          <div>
            <label className="block text-base text-white mb-4 font-light">
              Company
            </label>
            <input
              type="text"
              value={companyName}
              onChange={handleChange("companyName")}
              placeholder="Enter company name"
              className="w-full h-[53px] px-4 border rounded-[27px] text-white font-light bg-transparent border-[#E761A0] placeholder-gray-500 focus:ring-2 focus:ring-[#E761A0]/50 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-base text-white mb-4 font-light">
              Job Title
            </label>
            <input
              type="text"
              value={jobTitle}
              onChange={handleChange("jobTitle")}
              placeholder="Enter job title"
              className="w-full h-[53px] px-4 border rounded-[27px] text-white font-light bg-transparent border-[#E761A0] placeholder-gray-500 focus:ring-2 focus:ring-[#E761A0]/50 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-base text-white mb-4 font-light">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={handleChange("email")}
              onBlur={handleBlur("email")}
              placeholder="email@company.com"
              disabled={!isAdmin}
              className={`w-full h-[53px] px-4 border rounded-[27px] text-white font-light bg-transparent placeholder-gray-500 focus:ring-2 focus:ring-[#E761A0]/50 focus:outline-none transition-all duration-200 ${
                !isAdmin ? "cursor-not-allowed opacity-70" : ""
              } ${
                touched.email && fieldErrors.email
                  ? "border-red-500/50"
                  : "border-[#E761A0] hover:border-[#E761A0]"
              }`}
            />
            {touched.email && fieldErrors.email && (
              <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                <svg
                  className="w-4 h-4 shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                {fieldErrors.email}
              </p>
            )}
          </div>
          <div>
            <label className="block text-base text-white mb-4 font-light">
              Phone
            </label>
            <input
              type="tel"
              value={phone}
              onChange={handleChange("phone")}
              placeholder="Enter phone number (3–15 digits)"
              maxLength={15}
              className={`w-full h-[53px] px-4 border rounded-[27px] text-white font-light bg-transparent placeholder-gray-500 focus:ring-2 focus:ring-[#E761A0]/50 focus:outline-none ${
                touched.phone && fieldErrors.phone
                  ? "border-red-500/50"
                  : "border-[#E761A0] hover:border-[#E761A0]"
              }`}
            />
            {touched.phone && fieldErrors.phone && (
              <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                <svg
                  className="w-4 h-4 shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                {fieldErrors.phone}
              </p>
            )}
          </div>
          <div className="flex pt-[30px]">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="theme-btn min-w-[178px] xl:min-h-[54px] min-h-[45px] text-[12px] xl:text-[16px]"
            >
              {saving ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-5 h-5 border-2 border-t-transparent border-white rounded-full animate-spin" />
                  Updating...
                </span>
              ) : (
                "UPDATE"
              )}
            </button>
          </div>
        </div>

        <div className="flex flex-col">
          <label className="block text-base text-white font-light mb-6">
            Profile Image
          </label>
          <div className="flex flex-col items-start gap-[30px]">
            <div className="2xl:w-[218px] 2xl:h-[218px] h-[160px] w-[160px]  bg-transparent rounded-full border border-[#E761A0] flex items-center justify-center overflow-hidden shrink-0 p-3">
              <img
                src={displayProfileImg || undefined}
                alt="Profile"
                className={`${displayProfileImg === defaultProfileImage ? "w-[101px] h-[102px]" : "w-full h-full"} bg-black object-cover rounded-full`}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = defaultProfileImage;
                }}
              />
            </div>
            <div className="flex w-full max-w-[549px] h-[53px] rounded-full border border-[#868686] overflow-hidden  ">
              <button
                type="button"
                onClick={handleFileButtonClick}
                className="browse-file-btn shrink-0 px-9 h-full bg-white text-gray-800 text-base rounded-full font-semibold uppercase tracking-wide hover:bg-gray-100 transition-colors"
              >
                BROWSE FILE
              </button>
              <input
                type="text"
                readOnly
                placeholder="No file chosen"
                value={profileImageName}
                className="flex-1 min-w-0 h-full px-4 bg-transparent text-gray-400 text-sm placeholder-gray-500 focus:outline-none border-0"
              />
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp"
              className="hidden"
              onChange={handleFileChange}
            />
            {profileImageError && (
              <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                {profileImageError}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="mt-10">
        {/* <Link
          to="/change-password"
          className="inline-flex items-center justify-center gap-2 py-3 px-5 rounded-full text-sm font-bold text-white uppercase tracking-wide transition-opacity hover:opacity-90 w-fit"
          style={{ backgroundColor: "#E761A0" }}
        >
          <svg
            className="w-5 h-5 shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
            />
          </svg>
          Change password
        </Link> */}
      </div>
    </div>
  );
};

export default ManageAccount;
